/*======================================================================================================================
  * Bluebell: src/app/state/results-filters/results-filters.reducer.ts
  * Last Edited: 20/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { ResultsFilterActions } from '@state/results-filters/results-filter.actions';
import { AvailableResultsFilter, ResultsFilter } from '@state/results-filters/results-filter.interfaces';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';


export interface State extends EntityState<ResultsFilter> {
  activeFilterID: string|null;
  availableFilters: AvailableResultsFilter[];
}

const adapter = createEntityAdapter<ResultsFilter>({
  selectId: (filter: ResultsFilter) => filter.uuid,
});

export const initialState: State = adapter.getInitialState<State>({
  activeFilterID: null,
  availableFilters: [],
});

export const reducer = createReducer<State>(
  initialState,

  on(programmeProjectSelectionUnapplied, () => {
    return adapter.removeAll({ ...initialState });
  }),

  /**
   * CREATE NEW RESULTS FILTER
   *
   * When successfully creating a ResultsFilter, it adds the filter data
   * to the store with upsertOne and adds the filter name and uuid to the
   * list of availableFilters.
   */
  on(ResultsFilterActions.createSuccess, (state, { createdResultsFilter }) => {
    return adapter.upsertOne(
      createdResultsFilter,
      {
        ...state,
        availableFilters: [
          ...state.availableFilters,
          { name: createdResultsFilter.name, uuid: createdResultsFilter.uuid },
        ]
      }
    );
  }),

  /**
   * REPLACE STORED DATA FOR UPDATED FILTER
   */
  on(ResultsFilterActions.updateOneSuccess, (state, { updatedResultsFilter }) => {
    return adapter.upsertOne(updatedResultsFilter, state);
  }),

  /**
   * ON SUCCESSFUL DELETION OF A SAVED FILTER
   */
  on(ResultsFilterActions.deleteOne, (state, { uuid }) => {
    return adapter.removeOne(uuid, {
      ...state,
      availableFilters: state.availableFilters
        .filter(availableFilter => availableFilter.uuid !== uuid),
    });
  }),

  /**
   * ON SUCCESSFUL RENAMING OF A SAVED FILTER
   *
   * The filter should be updated in the entities and the available filters name
   * should be updated.
   */
  on(ResultsFilterActions.renameOneSuccess, (state, { renamedResultsFilter }) => {
    const { name, uuid } = renamedResultsFilter;
    const otherFilters = state.availableFilters.filter(availableFilter => availableFilter.uuid !== uuid);
    const updatedAvailableFilters: AvailableResultsFilter[] = [...otherFilters, { uuid, name }];

    return adapter.upsertOne(renamedResultsFilter, {
      ...state,
      availableFilters: updatedAvailableFilters
    });
  }),

  /*
   * LOAD AVAILABLE RESULTS FILTERS
   *
   * Sets the list of available filters in the store, or empties or on failure
   */
  on(ResultsFilterActions.loadAvailableSuccess, (state, { availableFilters }) => {
    return {
      ...state,
      availableFilters
    };
  }),
  on(ResultsFilterActions.loadAvailableFailure, (state) => {
    return {
      ...state,
      availableFilters: []
    };
  }),

  /**
   * LOAD FULL DATA FOR A SINGLE RESULTS FILTER
   *
   * on successful load of a filters data, uses upsertOne to add the filter to
   * the store.
   */
  on(ResultsFilterActions.loadOneSuccess, (state, { resultsFilter }) => {
    return adapter.upsertOne(resultsFilter, state);
  }),

  /**
   * SET FILTER AS THE ACTIVE FILTER
   */
  on(ResultsFilterActions.applyFilter, (state, newFilter) => {
    return adapter.upsertOne(newFilter, { ...state, activeFilterID: newFilter.uuid });
  }),

  /**
   * CLEAR THE CURRENTLY ACTIVE FILTER ID FIELD
   */
  on(ResultsFilterActions.clearFilter, (state) => {
    return { ...state, activeFilterID: null };
  }),

  /**
   * ENTIRELY CLEAR THE STORE OF RESULTS FILTERS
   */
  on(ResultsFilterActions.clear, (state) => {
    return adapter.removeAll({
      ...state,
      activeFilterID: null,
    });
  }),
);

export const {
  selectIds: getIds,
  selectEntities: getEntities,
  selectAll: getAll,
  selectTotal: getTotal,
} = adapter.getSelectors();

export const featureKey = 'resultsFilters';
export const getActiveFilterId = (state: State) => state.activeFilterID;
export const getAvailableFilters = (state: State) => state.availableFilters;
